import clsx from "clsx";
import { useRouter } from "next/router";
import { Fragment, HtmlHTMLAttributes, useEffect } from "react";
import { twMerge } from "tailwind-merge";
import { useLockBodyScroll } from "hooks";
import useTailwind from "hooks/useTailwind";
import { ChevronLeft } from "icons";
import { ComponentAtomsLink, ComponentMoleculesNavResource } from "types/graphql/generated";
import { useMegaNavMobileContext } from "../hooks/useMegaNavMobileContext/useMegaNavMobileContext";
import { MenuDrawerMobile } from "../MenuDrawerMobile";
import { MobileBrickTab } from "../MobileBrickTab";
import { MobileMenuHeaderLabel } from "../MobileMenuHeaderLabel";
import { SubMenuPanelGroup } from "../SubMenuPanelGroup";
import { SubMenuTabGroup } from "../SubMenuTabGroup";
import { MobileBottomCta } from "./MobileBottomCta/MobileBottomCta";
import { isAllowedToShowMobileCta } from "./utils";

interface MegaMenuResourcesProps extends HtmlHTMLAttributes<HTMLElement> {
  navResources: ComponentMoleculesNavResource[];
  getStartedButton: ComponentAtomsLink;
  loginButton: ComponentAtomsLink;
  isOpen: boolean;
}

const MegaMenuResourcesMobile = ({
  navResources,
  isOpen,
  getStartedButton,
  loginButton,
  className,
}: MegaMenuResourcesProps) => {
  const { activeTabIndex, activePanelIndex, setActivePanelIndex, goBack, setMobileMenuOpen } =
    useMegaNavMobileContext();
  const router = useRouter();
  const tailwind = useTailwind();

  const stateClasses = clsx({
    "translate-x-0": isOpen,
  });

  useLockBodyScroll(isOpen);

  useEffect(() => {
    setTimeout(() => {
      setActivePanelIndex(-1);
    }, 500);
  }, [isOpen, setActivePanelIndex]);

  const activeTabResource = navResources?.[activePanelIndex]?.subMenuTabs?.[activeTabIndex];

  const shouldShowMobileCta = isAllowedToShowMobileCta(activeTabResource);

  const headerDiff = shouldShowMobileCta ? tailwind.theme.height["mobile-cta"] : "0px";

  const contentWrapperClasses = clsx({
    relative: true,
  });

  const handleOnClick = (index: number) => {
    if (!navResources[index]?.url) {
      return setActivePanelIndex(index);
    }

    setMobileMenuOpen(false);
  };

  const handleHashChangeStart = () => {
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    router.events.on("hashChangeStart", handleHashChangeStart);

    return () => router.events.off("hashChangeStart", handleHashChangeStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <nav className={className}>
      {activePanelIndex > -1 && isOpen && (
        <button
          type="button"
          className="absolute left-0 top-0 flex h-navbar w-1/2 animate-fade items-center gap-1 bg-fog-00 pl-6 font-semibold"
          onClick={goBack}
        >
          <ChevronLeft className="h-3 w-3" />
          Back
        </button>
      )}
      <div
        className={twMerge(
          "fixed left-0 mt-navbar translate-x-full bg-fog-00 transition-all duration-300 ease-in-out",
          stateClasses
        )}
        style={{
          height: `calc(100dvh - ${tailwind.theme.height.navbar}`,
        }}
      >
        <div className={contentWrapperClasses} style={{ height: `calc(100% - ${headerDiff} )` }}>
          <div className="flex w-screen flex-col">
            {navResources.map((resource, index) => (
              <MobileBrickTab
                href={resource.url}
                title={resource.label}
                id={resource.id}
                onClick={() => handleOnClick(index)}
                key={resource.id}
              />
            ))}
          </div>

          <MenuDrawerMobile isOpen={activePanelIndex > -1}>
            {navResources.map(
              (resource, index) =>
                index === activePanelIndex &&
                !resource.url && (
                  <Fragment key={resource.id}>
                    {resource.subMenuTabs.length > 0 ? (
                      <>
                        <MobileMenuHeaderLabel label={resource.label} />
                        <SubMenuTabGroup resource={resource} />
                      </>
                    ) : (
                      <>
                        <MobileMenuHeaderLabel label={resource.label} />
                        <SubMenuPanelGroup panels={resource.subMenuPanels.slice(0, 3)} />
                      </>
                    )}
                  </Fragment>
                )
            )}
          </MenuDrawerMobile>
        </div>

        <MobileBottomCta
          shouldHide={!shouldShowMobileCta}
          getStartedButton={getStartedButton}
          loginButton={loginButton}
        />
      </div>
    </nav>
  );
};

export { MegaMenuResourcesMobile };
